/* eslint-disable no-unused-vars */
import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import {
  fetchEmergencyContact,
  createEmergencyContact,
  updateEmergencyContact,
  deleteEmergencyContact,
  resetEmergencyContact,
  // Imported the completeness check action
  checkEmployeeCompleteness,
} from "../../store/slices/emergencyContactSlice";
import { fetchEmployeeById } from "../../store/slices/employeeDetailSlice";

export const RelationshipType = [
  'Spouse', 'Mother', 'Father', 'Sibling', 'Child', 'Relative', 'Friend', 'Other', 'Live-in Partner'
];

const EmployeeEmergencyContact = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { id: employeeId } = useParams();
  
  // Get global status from slice to trigger the overlay
  const { contacts = [], status } = useSelector((state) => state.employeeEmergencyContact || {});

  const [localContacts, setLocalContacts] = useState([]);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (employeeId) {
      dispatch(fetchEmergencyContact(employeeId));
    }
    return () => dispatch(resetEmergencyContact());
  }, [employeeId, dispatch]);

  useEffect(() => {
    const savedRows = contacts.map(c => ({ ...c, editing: false }));
    setLocalContacts(savedRows);
  }, [contacts]);

  useImperativeHandle(ref, () => ({
    submit: async () => true 
  }));

  const showStatus = (text, type = "success") => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
  };

  const handleAddRow = () => {
    setLocalContacts(prev => [
      ...prev, 
      { 
        firstName: "", 
        middleName: "", 
        lastName: "", 
        relationship: "", 
        phone: "", 
        email: "", 
        address: "", 
        emergencyContact: false, 
        editing: true 
      }
    ]);
  };

  const toggleEdit = (index) => {
    setLocalContacts(prev => prev.map((c, i) => i === index ? { ...c, editing: !c.editing } : c));
  };

  const handleInputChange = (index, name, value) => {
    setLocalContacts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
    if (statusMsg.text) setStatusMsg({ text: "", type: "" });
  };

  const handleSaveRow = async (row, index) => {
    if (!row.firstName?.trim() || !row.lastName?.trim() || !row.relationship || !row.phone) {
      showStatus("Missing required fields.", "danger");
      return;
    }

    setIsProcessing(true); // Trigger global overlay
    try {
      const payload = {
        employeeId,
        firstName: row.firstName,
        middleName: row.middleName,
        lastName: row.lastName,
        emergencyContact: row.emergencyContact || false,
        relationship: row.relationship,
        phone: row.phone,
        email: row.email,
        address: row.address
      };

      if (row.id) {
        await dispatch(updateEmergencyContact({ id: row.id, contactData: payload })).unwrap();
        showStatus("Updated successfully!");
      } else {
        await dispatch(createEmergencyContact({ employeeId, contactData: payload })).unwrap();
        showStatus("Added successfully!");
      }
      
      // TRIGGER: Completeness Check API
      await dispatch(checkEmployeeCompleteness(employeeId)).unwrap();
dispatch(fetchEmployeeById(employeeId));
      dispatch(fetchEmergencyContact(employeeId));
    } catch (err) {
      showStatus(err?.message || "Save failed.", "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelRow = (index, row) => {
    if (!row.id) {
      setLocalContacts(prev => prev.filter((_, i) => i !== index));
    } else {
      const original = contacts.find(c => c.id === row.id);
      setLocalContacts(prev => prev.map((c, i) => i === index ? { ...original, editing: false } : c));
    }
    setStatusMsg({ text: "", type: "" });
  };

  const handleRemoveRow = async (index, id) => {
    if (!id) {
      setLocalContacts(prev => prev.filter((_, i) => i !== index));
      return;
    }
    if (!window.confirm("Delete this emergency contact?")) return;
    
    setIsProcessing(true);
    try {
      await dispatch(deleteEmergencyContact(id)).unwrap();

      // TRIGGER: Completeness Check API after deletion
      await dispatch(checkEmployeeCompleteness(employeeId)).unwrap();

      showStatus("Deleted successfully!");
      dispatch(fetchEmergencyContact(employeeId));
    } catch (err) {
      showStatus("Failed to delete record.", "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const inputStyle = (editing) => ({
    height: "32px",
    fontSize: "0.8rem",
    borderRadius: '8px',
    border: !editing ? 'none' : '1px solid rgba(0,0,0,0.1)',
    backgroundColor: !editing ? 'transparent' : '#ffffff',
    pointerEvents: !editing ? 'none' : 'auto',
  });

  const headerStyle = { 
    backgroundColor: "#1e293b", 
    color: "#fff", 
    fontSize: "0.7rem", 
    fontWeight: "700", 
    letterSpacing: "0.05em",
    border: "none"
  };

  // const isLoading = status === 'loading' || isProcessing;
  const isLoading = isProcessing;

  return (
    <div className="container-fluid py-2 px-0">
      <style>{`
        .PhoneInput { background-color: transparent; border-radius: 8px; padding: 0 4px; height: 32px; }
        .editing-phone { background-color: #ffffff !important; border: 1px solid rgba(0,0,0,0.1) !important; }
        .PhoneInputInput { border: none !important; font-size: 0.8rem !important; outline: none !important; background: transparent; }
        .view-mode-input { font-weight: 500; color: #212529; text-transform: capitalize; appearance: none; }
        .form-select:disabled { opacity: 1; color: #212529; background-image: none; }
        .progress-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.7); z-index: 100; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
      `}</style>

      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        {/* Global Loading Overlay same with other screens */}
        {isLoading && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <div className="d-flex justify-content-end align-items-center px-3 py-2 rounded-2 mb-3" style={{ backgroundColor: "#1e293b", minHeight: '44px' }}>
          <div className="d-flex align-items-center gap-3">
            {statusMsg.text && (
              <span className={`fw-bold text-uppercase d-flex align-items-center text-${statusMsg.type}`} style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                <i className={`bi ${statusMsg.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                {statusMsg.text}
              </span>
            )}
            <button className="btn btn-sm d-flex align-items-center px-3 text-white border-0 shadow-none" style={{ backgroundColor: "#198754", borderRadius: "6px", fontWeight: "600", fontSize: '0.75rem', height: '28px' }} onClick={handleAddRow} disabled={isLoading}>
              <i className="bi bi-plus-circle me-2"></i> Add
            </button>
          </div>
        </div>

        <div className="table-responsive rounded-2" style={{ border: "1px solid #dee2e6", overflow: "hidden" }}>
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th className="py-3 ps-4" style={{ ...headerStyle, width: '40px' }}>Emergency Contact</th>
                <th className="py-3" style={headerStyle}>FULL NAME</th>
                <th className="py-3" style={headerStyle}>RELATIONSHIP</th>
                <th className="py-3" style={headerStyle}>PHONE</th>
                <th className="py-3" style={headerStyle}>EMAIL</th>
                <th className="py-3" style={headerStyle}>ADDRESS</th>
                <th className="py-3 pe-4 text-end" style={{ ...headerStyle, width: "110px" }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {localContacts.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-5 text-muted small fw-semibold">No employee contacts found.</td></tr>
              ) : (
                localContacts.map((row, index) => (
                  <tr key={row.id || `new-${index}`} className={row.editing ? "bg-primary bg-opacity-10" : ""}>
                    <td className="ps-4 text-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input shadow-none" 
                        checked={row.emergencyContact} 
                        onChange={(e) => handleInputChange(index, "emergencyContact", e.target.checked)}
                        disabled={!row.editing || isLoading}
                        style={{ width: '16px', height: '16px', cursor: (row.editing && !isLoading) ? 'pointer' : 'default' }}
                      />
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <input className={`form-control ${!row.editing ? 'view-mode-input' : ''}`} style={inputStyle(row.editing)} value={row.firstName} onChange={(e) => handleInputChange(index, "firstName", e.target.value)} placeholder="First" disabled={isLoading} />
                        <input className={`form-control ${!row.editing ? 'view-mode-input' : ''}`} style={inputStyle(row.editing)} value={row.middleName} onChange={(e) => handleInputChange(index, "middleName", e.target.value)} placeholder="Middle" disabled={isLoading} />
                        <input className={`form-control ${!row.editing ? 'view-mode-input' : ''}`} style={inputStyle(row.editing)} value={row.lastName} onChange={(e) => handleInputChange(index, "lastName", e.target.value)} placeholder="Last" disabled={isLoading} />
                      </div>
                    </td>
                    <td>
                      <select 
                        className={`form-select form-select-sm ${!row.editing ? 'view-mode-input' : ''}`} 
                        style={inputStyle(row.editing)} 
                        value={row.relationship} 
                        onChange={(e) => handleInputChange(index, "relationship", e.target.value)}
                        disabled={!row.editing || isLoading}
                      >
                        <option value="" disabled>Select</option>
                        {RelationshipType.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </td>
                    <td><PhoneInput className={`PhoneInput ${row.editing ? 'editing-phone' : ''}`} defaultCountry="PH" value={row.phone} onChange={(v) => handleInputChange(index, "phone", v)} disabled={!row.editing || isLoading} /></td>
                    <td><input className={`form-control ${!row.editing ? 'view-mode-input' : ''}`} style={inputStyle(row.editing)} value={row.email} onChange={(e) => handleInputChange(index, "email", e.target.value)} disabled={isLoading} /></td>
                    <td><input className={`form-control ${!row.editing ? 'view-mode-input' : ''}`} style={inputStyle(row.editing)} value={row.address} onChange={(e) => handleInputChange(index, "address", e.target.value)} disabled={isLoading} /></td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {row.editing ? (
                          <>
                            <button className="btn btn-success btn-sm py-0 px-2 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => handleSaveRow(row, index)} disabled={isLoading}>
                                <i className="bi bi-check-lg"></i>
                            </button>
                            <button className="btn btn-outline-secondary btn-sm py-0 px-2 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => (row.id ? toggleEdit(index) : handleCancelRow(index, row))} disabled={isLoading}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-sm btn-outline-primary py-0 px-2 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => toggleEdit(index)} disabled={isLoading}>
                                <i className="bi bi-pencil-square"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger py-0 px-2 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }} onClick={() => handleRemoveRow(index, row.id)} disabled={isLoading}>
                                <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

export default EmployeeEmergencyContact;